/** Kurzes Onboarding als Story-Auftakt — danach übernimmt die Kampagne (storyCampaign). */

import { hasLeitstelleOnMap } from "./worldChecks";

export type TourTarget =
  | "stat-budget"
  | "stat-renown"
  | "stat-influence"
  | "stat-radius"
  | "build"
  | "catalog"
  | "dock-station"
  | "hr"
  | "hr-recruit-tab"
  | "hr-recruit"
  | "hr-pool-tab"
  | "hr-pool"
  | "hr-applicant-invite"
  | "hr-applicant-hire"
  | "station-nav-overview"
  | "station-nav-fleet"
  | "station-nav-shop"
  | "station-nav-crew"
  | "station-list"
  | "station-shop"
  | "station-crew-board"
  | "station-crew-picker"
  | "station-crew-fill"
  | "hr-assign-tab"
  | "hr-assign-board"
  | "incident-list"
  | "incident-alarm"
  | "verwaltung"
  | "stat-progress"
  | "funk-panel"
  | "citizen-grant"
  | "incident-map"
  | "incident-map-tools";

export type TourStep = {
  id: string;
  title: string;
  body: string;
  target: TourTarget;
  ready: (s: SnapFlags) => boolean;
  waitHint?: string;
  action?:
    | "focus_radius"
    | "open_build"
    | "open_station"
    | "open_incident"
    | "open_catalog"
    | "open_progress"
    | "open_citizen"
    | "station_overview"
    | "station_fleet"
    | "station_crew"
    | "none";
  ctaLabel?: string;
  forceRadius?: boolean;
  finish?: boolean;
};

export type SnapFlags = {
  hasLs: boolean;
  hasStation: boolean;
  openIncident: boolean;
  busy: boolean;
  resolved: boolean;
  budget: number;
  renown: number;
  influence: number;
  cityKm: number;
  covKm: number;
  orgLabel: string;
  firstLabel: string;
};

type Snap = {
  world: {
    id?: string;
    tutorialStep: number;
    playerLevel?: number;
    hasLeitstelle?: boolean;
    budget: number;
    renown?: number;
    influence?: number;
    startOrg?: string;
    coverageKm?: number;
    radiusKm?: number;
    story?: { done?: string[]; active?: unknown };
  };
  buildings: Array<{ kind: string }>;
  vehicles: Array<{ status: string }>;
  incidents: Array<{ status: string }>;
};

const ORG_LABEL: Record<string, string> = {
  fire: "Feuerwehr",
  police: "Polizei",
  ems: "Rettungsdienst",
};
const ORG_KIND: Record<string, string> = {
  fire: "fire_station",
  police: "police_station",
  ems: "ems_station",
};
const ORG_FIRST: Record<string, string> = {
  fire: "Freiwillige Feuerwehr",
  police: "Polizeirevier",
  ems: "Rettungswache",
};

export const TOUR_STORAGE_KEY = "bda-tour-index";
export const TOUR_SKIP = 900;

export function tourStorageKey(worldId?: string | null) {
  return worldId ? `${TOUR_STORAGE_KEY}:${worldId}` : TOUR_STORAGE_KEY;
}

export function readTourIndex(worldId?: string | null): number {
  if (typeof localStorage === "undefined") return 0;
  try {
    if (worldId) {
      const raw = localStorage.getItem(tourStorageKey(worldId));
      if (raw != null && raw !== "") {
        const n = Number(raw);
        return Number.isFinite(n) ? n : 0;
      }
      // Neue Welt: nie den globalen Skip einer alten Welt übernehmen
      return 0;
    }
    const n = Number(localStorage.getItem(TOUR_STORAGE_KEY) || "0");
    return Number.isFinite(n) ? n : 0;
  } catch {
    return 0;
  }
}

export function persistTourIndex(index: number, worldId?: string | null) {
  if (typeof localStorage === "undefined") return;
  try {
    localStorage.setItem(tourStorageKey(worldId), String(index));
    // Legacy-Key mitziehen, damit alte Leser nicht verwirrend bleiben
    if (!worldId) localStorage.setItem(TOUR_STORAGE_KEY, String(index));
  } catch {
    /* ignore */
  }
}

export function snapFlags(snap: Snap): SnapFlags {
  const org = snap.world.startOrg ?? "fire";
  const startKind = ORG_KIND[org] ?? "fire_station";
  const ops = snap.buildings.filter((b) =>
    ["fire_station", "police_station", "ems_station", "thw"].includes(b.kind),
  );
  return {
    hasLs: hasLeitstelleOnMap(snap),
    hasStation: ops.some((b) => b.kind === startKind),
    openIncident: (snap.incidents ?? []).some((i) => i.status === "open" || i.status === "active"),
    busy: (snap.vehicles ?? []).some((v) => v.status === "enroute" || v.status === "onscene"),
    resolved: (snap.incidents ?? []).some((i) => i.status === "resolved"),
    budget: snap.world.budget,
    renown: snap.world.renown ?? 0,
    influence: snap.world.influence ?? 0,
    cityKm: snap.world.radiusKm ?? 10,
    covKm: snap.world.coverageKm ?? snap.world.radiusKm ?? 10,
    orgLabel: ORG_LABEL[org] ?? "Feuerwehr",
    firstLabel: ORG_FIRST[org] ?? "erste Wache",
  };
}

/** Kurz: Karte → Leiste erklären → Leitstelle → Wache → erste Lage. */
export function buildTourSteps(f: SnapFlags): TourStep[] {
  return [
    {
      id: "aufbauplan",
      title: "Stadtgebiet auf der Karte",
      body: `Sie übernehmen die BOS-Disposition für diese Stadt.\n\nOben „Stadtgebiet“ zeigt die gelbe Grenze (~${f.cityKm.toFixed(1)} km). Darin platzieren Sie Leitstelle und Wachen.\n\nDen Button können Sie an- und ausschalten, um die Grenze hervorzuheben.`,
      target: "stat-radius",
      ready: () => true,
      action: "focus_radius",
      forceRadius: true,
      ctaLabel: "Weiter",
    },
    {
      id: "budget",
      title: "Budget",
      body: `Ihr Geld für Bau, Fahrzeuge und Werbung.\n\nStart: ${f.budget.toLocaleString("de-DE")} € — Mittel kommen über Zuweisungen und Einsätze.\n\nSpäter: Antippen öffnet Finanzen (Kredite, Buchungen). Jetzt nur merken, wo es steht.`,
      target: "stat-budget",
      ready: () => true,
      action: "none",
      ctaLabel: "Weiter",
    },
    {
      id: "ansehen",
      title: "Ansehen",
      body: `Ihr Ruf bei Stadt und Bürgern.\n\nGutes Ansehen hilft später bei Werbung und Förderungen — Details schauen wir uns später an.`,
      target: "stat-renown",
      ready: () => true,
      action: "none",
      ctaLabel: "Weiter",
    },
    {
      id: "einfluss",
      title: "Einfluss",
      body: `Politisches Kapital für Skills und Rathaus-Anträge.\n\nMehr Einfluss = mehr Freischaltungen unter Level & Rang. Erstmal nur: hier sehen Sie den Stand.`,
      target: "stat-influence",
      ready: () => true,
      action: "none",
      ctaLabel: "Weiter",
    },
    {
      id: "level",
      title: "Level & Rang",
      body: `Ihr Fortschritt als Leiter — Einsätze bringen Erfahrung, Skills schalten neue Fähigkeiten frei.\n\nGenauer schauen wir uns das später an — erstmal nur, wo Sie es finden.`,
      target: "stat-progress",
      ready: () => true,
      action: "none",
      ctaLabel: "Weiter",
    },
    {
      id: "leitstelle",
      title: "Leitstelle in Betrieb",
      body: `Als Nächstes erscheint der Zuweisungsbescheid. Unterschreiben, dann unten „Bauen“ → Führung → BOS-Leitstelle im gelben Stadtgebiet setzen.`,
      target: "build",
      ready: (s) => s.hasLs,
      waitHint:
        "Zuweisung unterschreiben (großes Fenster), dann unten „Bauen“ → BOS-Leitstelle im Stadtgebiet setzen.",
      action: "none",
      forceRadius: true,
      ctaLabel: "Unten: Bauen",
    },
    {
      id: "brandschutz",
      title: `Erste ${f.firstLabel}`,
      body: `Die Leitstelle steht. Unterschreiben Sie die Wachen-Zuweisung, dann unten „Bauen“ die erste ${f.firstLabel} setzen.`,
      target: "build",
      ready: (s) => s.hasStation,
      waitHint: `Wachen-Zuweisung unterschreiben, dann unten „Bauen“ → erste ${f.firstLabel} setzen.`,
      action: "none",
      forceRadius: true,
      ctaLabel: "Unten: Bauen",
    },
    {
      id: "wache-kurz",
      title: "Wache im Blick",
      body: "Unten im Dock: Wachen → Wache öffnen. Dort Fuhrpark und Personal. Besatzung dem Fahrzeug zuweisen — sonst erscheint „kein Personal“ und Alarmieren geht nicht.",
      target: "dock-station",
      ready: (s) => s.hasStation,
      action: "open_station",
      ctaLabel: "Wache öffnen",
    },
    {
      id: "erste-lage",
      title: "Erste Lage meistern",
      body: "Rechts erscheint der erste Einsatz. Öffnen, alarmieren, abarbeiten — danach übernimmt die Kampagne links.",
      target: "incident-list",
      ready: (s) => s.openIncident || s.busy || s.resolved,
      waitHint:
        "Noch kein Einsatz in der Liste. Warten Sie kurz — sobald rechts eine Lage auftaucht, öffnen Sie sie.",
      action: "open_incident",
      ctaLabel: "Einsatz öffnen",
    },
    {
      id: "handoff",
      title: "Kampagne übernimmt",
      body: "Der Aufbauplan läuft weiter links. Spätere Bescheide erscheinen automatisch, wenn Sie soweit sind.",
      target: "incident-list",
      ready: () => true,
      action: "none",
      ctaLabel: "Los geht’s",
      finish: true,
    },
  ];
}

/** UI-Erklär-Schritte — Verträge und Finanz-Panel erst danach */
export const TOUR_UI_INTRO_STEP_IDS = [
  "aufbauplan",
  "budget",
  "ansehen",
  "einfluss",
  "level",
] as const;

export type TourUiIntroStepId = (typeof TOUR_UI_INTRO_STEP_IDS)[number];

export function isTutorialActive(snap: Snap, tourIndex: number): boolean {
  if (tourIndex >= TOUR_SKIP) return false;
  const steps = buildTourSteps(snapFlags(snap));
  if (tourIndex >= steps.length) return false;
  return true;
}

export function isTourUiIntroStep(snap: Snap, tourIndex: number): boolean {
  const tour = getTourStep(snap, tourIndex);
  if (!tour) return false;
  return (TOUR_UI_INTRO_STEP_IDS as readonly string[]).includes(tour.step.id);
}

/** Stat-Klicks (Finanzen/Level) während des gesamten Aufbauplans sperren */
export function blockStatPanelsDuringTour(snap: Snap, tourIndex: number): boolean {
  return isTutorialActive(snap, tourIndex);
}

export function getTourStep(snap: Snap, tourIndex: number) {
  if (!isTutorialActive(snap, tourIndex)) return null;
  const f = snapFlags(snap);
  const steps = buildTourSteps(f);
  const i = Math.max(0, Math.min(tourIndex, steps.length - 1));
  const step = steps[i];
  return {
    step,
    index: i,
    total: steps.length,
    canAdvance: step.ready(f),
    flags: f,
  };
}

export function tourTargetSelector(target: TourTarget): string {
  return `[data-coach-target="${target}"]`;
}

/** Score für doppelte data-coach-target — innerstes / modal-spezifischstes gewinnt. */
function coachTargetScore(el: HTMLElement): number {
  const rect = el.getBoundingClientRect();
  if (rect.width < 2 || rect.height < 2) return -1;

  let score = 0;
  const manageOpen = !!document.querySelector(".manage-backdrop");
  const stationOpen = !!document.querySelector(".modal.station-modal");

  if (el.closest(".manage-sheet")) score += 1200;
  if (el.closest(".station-modal")) score += 1200;
  if (el.closest(".verwaltung-tabs")) score += 400;
  if (el.closest(".hr-flow-steps") || el.closest(".detail-nav")) score += 300;

  if (manageOpen && el.closest(".station-dock")) score -= 800;
  if (stationOpen && el.closest(".station-dock")) score -= 800;

  if (el.tagName === "BUTTON") score += 80;

  const area = rect.width * rect.height;
  if (area > 60_000) score -= 120;
  else if (area < 8_000) score += 40;

  return score;
}

/** Best visible coach target — bei doppelten data-coach-target (Dock vs. Modal-Tabs). */
export function queryCoachTarget(target: TourTarget): HTMLElement | null {
  const nodes = Array.from(document.querySelectorAll(tourTargetSelector(target))) as HTMLElement[];
  let best: HTMLElement | null = null;
  let bestScore = -1;
  for (const el of nodes) {
    const score = coachTargetScore(el);
    if (score > bestScore) {
      best = el;
      bestScore = score;
    }
  }
  return best ?? nodes[0] ?? null;
}

/**
 * Tour-Index an Welt anpassen.
 * Wichtig: Frische Welten starten den Aufbauplan immer neu — localStorage-Skip
 * einer alten Welt darf den Aufbauplan nicht killen.
 */
export function syncTourIndexFromSnapshot(snap: Snap, localIndex: number): number {
  const step = snap.world.tutorialStep ?? 0;
  const level = snap.world.playerLevel ?? 1;
  const doneLen = snap.world.story?.done?.length ?? 0;
  const f = snapFlags(snap);
  const steps = buildTourSteps(f);

  const virgin = !f.hasLs && !f.hasStation && doneLen === 0 && level <= 1 && step < 2;
  const leitstelleIdx = Math.max(
    0,
    steps.findIndex((s) => s.id === "leitstelle"),
  );

  if (virgin) {
    if (localIndex >= TOUR_SKIP || localIndex >= steps.length) return 0;
    // Erklären bis Leitstelle — nicht darüber hinaus springen
    return Math.min(Math.max(0, localIndex), leitstelleIdx);
  }

  if (localIndex >= TOUR_SKIP) return localIndex;

  if (step >= 5 || level >= 2 || doneLen > 0) return TOUR_SKIP;

  let fromServer = 0;
  if (!f.hasLs) fromServer = leitstelleIdx;
  else if (!f.hasStation) fromServer = Math.max(leitstelleIdx + 1, steps.findIndex((s) => s.id === "brandschutz"));
  else if (step < 3) fromServer = Math.max(0, steps.findIndex((s) => s.id === "wache-kurz"));
  else if (!f.openIncident && !f.busy && !f.resolved)
    fromServer = Math.max(0, steps.findIndex((s) => s.id === "erste-lage"));
  else fromServer = Math.max(0, steps.findIndex((s) => s.id === "handoff"));

  if (fromServer < 0 || fromServer >= steps.length) return TOUR_SKIP;
  return Math.max(localIndex, fromServer);
}
